// Client/src/store/authStore.ts
// Bindet den zentralen SessionManager in einen Zustandsspeicher für React ein.

import { create } from 'zustand';
import { sessionManager, SessionSnapshot, SessionStore, SessionUser, NativeLoginResult } from '../core/SessionManager';

// ---------------------------
// 1) Typendefinitionen
// ---------------------------
type User = SessionUser;

interface Store extends SessionStore {
  storeId: string;
  name: string;
  location: string;
}

interface AuthState {
  user: User | null;
  token: string | null;           
  selectedStoreId: string | null;
  stores: Store[];
  status: SessionSnapshot['status'];
  error: string | null;

  // Login + Token abfragen
  login: (opts: {
    employeeId: string;
    password?: string;
    qr_token?: string;
  }) => Promise<void>;

  fetchTokenAndUser: () => Promise<void>;
  loginNative: (payload: { store_id: string; public_key: string; token: string; username: string; dev_sim?: boolean }) => Promise<NativeLoginResult>;

  // Filialen-Handling
  setSelectedStore: (storeId: string | null) => void;
  setStores: (stores: Store[]) => void;
  getselectedStore: () => void;

  // Logout
  logout: () => Promise<void>;

  // Permissions
  hasPermission: (perm: 'createTask' | 'manageUsers' | 'manageStores') => boolean;
  isCleaningStaff: () => boolean;
  isCleaningManager: () => boolean;
  isCleaningPortalUser: () => boolean;
  isAnonymousGuest: () => boolean;

  // Profil abrufen
  fetchUser: () => Promise<void>;
}

// ---------------------------
// 2) Store-Implementierung
// ---------------------------
export const useAuthStore = create<AuthState>((set, get) => {
  const syncWithSession = (snapshot: SessionSnapshot) => {
    set({
      user: snapshot.user,
      token: snapshot.token,
      stores: snapshot.stores as Store[],
      selectedStoreId: snapshot.selectedStoreId,
      status: snapshot.status,
      error: snapshot.error,
    });
  };

  const initialSnapshot = sessionManager.getSnapshot();
  sessionManager.subscribe(syncWithSession);

  return {
    user: initialSnapshot.user,
    token: initialSnapshot.token,
    selectedStoreId: initialSnapshot.selectedStoreId,
    stores: initialSnapshot.stores as Store[],
    status: initialSnapshot.status,
    error: initialSnapshot.error,

    /**
     * Login via zentralem SessionManager.
     */
    login: async ({ employeeId, password, qr_token }) => {
      await sessionManager.login({ employeeId, password, qr_token });
    },

    /**
     * Lädt Token & Benutzerinformationen vom Server (Bootstrap).
     */
    fetchTokenAndUser: async () => {
      try {
        await sessionManager.bootstrapFromServer();
      } catch (error) {
        console.error('Fehler bei fetchTokenAndUser:', error);
        if (get().status === 'authenticated') {
          await sessionManager.logout();
        }
      }
    },

    /**
     * Native-Client-Login über den SessionManager.
     */
    loginNative: async (payload) => {
      return sessionManager.loginNative(payload);
    },

    /**
     * Setzt die aktuelle Filiale über den SessionManager.
     */
    setSelectedStore: (storeId) => {
      sessionManager.setSelectedStore(storeId);
    },

    /**
     * Stellt sicher, dass eine gültige Filiale ausgewählt ist.
     */
    getselectedStore: () => {
      sessionManager.ensureSelectedStore();
    },

    /**
     * Aktualisiert die verfügbaren Filialen zentral.
     */
    setStores: (stores) => {
      sessionManager.updateStores(stores);
    },

    /**
     * Logout über SessionManager; Cookie/State werden server- & clientseitig entfernt.
     */
    logout: async () => {
      await sessionManager.logout();
    },

    /**
     * Einfache Rollenprüfung.
     */
    hasPermission: (permission) => {
      const { user } = get();
      if (!user) return false;

      switch (permission) {
        case 'createTask':
          return user.role !== 'reinigungskraft';
        case 'manageUsers':
          return ['filialleiter', 'admin', 'manager_reinigungskraft', 'dm_Reinigung'].includes(user.role);
        case 'manageStores':
          return user.role === 'admin';
        default:
          return false;
      }
    },

    /**
     * Prüft, ob der aktuelle Benutzer eine Reinigungskraft ist.
     */
    isCleaningStaff: () => {
      const { user } = get();
      return user?.role === 'reinigungskraft';
    },

    /**
     * Prüft, ob der aktuelle Benutzer eine Reinigungsmanager-Rolle hat.
     */
    isCleaningManager: () => {
      const { user } = get();
      return user ? ['manager_reinigungskraft', 'dm_Reinigung'].includes(user.role) : false;
    },

    /**
     * Prüft, ob der Benutzer Teil des Reinigung-Portals ist (Kraft oder Manager).
     */
    isCleaningPortalUser: () => {
      const { isCleaningStaff, isCleaningManager } = get();
      return isCleaningStaff() || isCleaningManager();
    },

    isAnonymousGuest: () => {
      const { user } = get();
      return user?.role === 'anonymous_guest' || user?.isAnonymousGuest === true;
    },

    /**
     * Aktualisiert Benutzerprofil zentral.
     */
    fetchUser: async () => {
      try {
        await sessionManager.refreshUserProfile();
      } catch (error) {
        console.error('fetchUser Fehler:', error);
        await sessionManager.logout();
      }
    },
  };
});
